import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { FileUpload } from "@/components/BookingParts";
import { usePrices } from "@/pages/Pricing";

const SLOTS = ["08-10", "10-12", "12-14", "14-16", "16-18", "18-20"];
const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function Step({ n, title, done, children, testId, required }) {
  return (
    <section className="w-card p-5 md:p-6" data-testid={testId}>
      <h2 className="text-lg font-bold flex items-center gap-3 mb-4">
        {done
          ? <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-mint-ink text-white shrink-0" aria-label="erledigt"><CheckCircle2 size={18} /></span>
          : <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-mint-soft text-mint-ink font-extrabold text-sm shrink-0 border border-mint" aria-label={`Schritt ${n}, offen`}>{n}</span>}
        <span>{title}{required && <span className="text-mint-ink" title="Pflichtfeld" aria-label="Pflichtfeld"> *</span>}</span>
        {!done && required && <span className="ml-auto text-xs font-semibold text-ink-2 bg-gray-100 rounded-full px-2 py-0.5">offen</span>}
      </h2>
      {children}
    </section>
  );
}

const save = async (path, body, onSaved) => {
  try { const { data } = await api.put(path, body); toast.success("Gespeichert"); onSaved(data); } catch (e) { toast.error(errMsg(e)); }
};

function ProfileForm({ p, onSaved }) {
  const pr = p.private || {};
  const [f, setF] = useState({ display_name: p.display_name || "", bio: p.bio || "", street: pr.street || "", plz: pr.plz || "", phone: pr.phone || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="grid sm:grid-cols-2 gap-4" onSubmit={(e) => { e.preventDefault(); save("/provider/me/profile", f, onSaved); }}>
      <label className="block"><span className="w-label">Anzeigename</span><input className="w-input" required value={f.display_name} onChange={set("display_name")} data-testid="ob-display-name" /></label>
      <label className="block"><span className="w-label">Telefon (privat)</span><input className="w-input" value={f.phone} onChange={set("phone")} data-testid="ob-phone" /></label>
      <label className="block sm:col-span-2"><span className="w-label">Über dich</span><textarea className="w-input !h-24 py-3" value={f.bio} onChange={set("bio")} data-testid="ob-bio" /></label>
      <label className="block"><span className="w-label">Straße (privat, erst nach Bestätigung sichtbar)</span><input className="w-input" required value={f.street} onChange={set("street")} data-testid="ob-street" /></label>
      <label className="block"><span className="w-label">PLZ</span><input className="w-input" required inputMode="numeric" maxLength={5} value={f.plz} onChange={set("plz")} data-testid="ob-plz" /></label>
      <button className="w-btn w-btn-primary sm:col-span-2 sm:w-fit" data-testid="ob-profile-save">Speichern</button>
    </form>
  );
}

function KycForm({ p, onSaved }) {
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/provider/me/kyc", fd); toast.success("Identität verifiziert (Mock-KYC)"); onSaved(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="w-muted">Lade Personalausweis oder Reisepass hoch. In v1 prüft ein Mock-Anbieter (später IDnow/Sumsub). Status: <strong data-testid="ob-kyc-status">{p.kyc_status || "offen"}</strong></p>
      <FileUpload label="Ausweis hochladen" onFile={upload} testId="ob-kyc-upload" />
    </div>
  );
}

function BusinessForm({ p, onSaved }) {
  const pr = p.private || {};
  const [f, setF] = useState({ business_type: p.business_type || "private", acts_as: p.acts_as || (p.business_type === "commercial" ? "unternehmer" : "privat"), company_name: pr.company_name || "", tax_id: pr.tax_id || "" });
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save("/provider/me/business", f, onSaved); }}>
      <div className="grid grid-cols-2 gap-2">
        {[["private", "Privatperson"], ["commercial", "Gewerblich"]].map(([v, l]) => (
          <label key={v} className={`w-card p-3 flex gap-2 items-center cursor-pointer ${f.business_type === v ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
            <input type="radio" checked={f.business_type === v} onChange={() => setF({ ...f, business_type: v })} data-testid={`ob-business-${v}`} /> {l}
          </label>
        ))}
      </div>
      <fieldset className="space-y-2">
        <legend className="w-label">Du handelst rechtlich als</legend>
        <p className="w-muted text-sm">Diese Angabe ist für die Verbraucherrechte deiner Kunden maßgeblich. Wer dauerhaft oder wiederholt gegen Entgelt Leistungen anbietet, handelt regelmäßig als Unternehmer.</p>
        <div className="grid grid-cols-2 gap-2">
          {[["privat", "Privatperson"], ["unternehmer", "Unternehmer"]].map(([v, l]) => (
            <label key={v} className={`w-card p-3 flex gap-2 items-center cursor-pointer ${f.acts_as === v ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
              <input type="radio" checked={f.acts_as === v} onChange={() => setF({ ...f, acts_as: v })} data-testid={`ob-actsas-${v}`} /> {l}
            </label>
          ))}
        </div>
      </fieldset>
      {f.business_type === "commercial" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="w-label">Firmenname</span><input className="w-input" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="ob-company" /></label>
          <label className="block"><span className="w-label">USt-IdNr. (optional)</span><input className="w-input" value={f.tax_id} onChange={(e) => setF({ ...f, tax_id: e.target.value })} data-testid="ob-taxid" /></label>
        </div>
      )}
      <button className="w-btn w-btn-primary" data-testid="ob-business-save">Speichern</button>
    </form>
  );
}

const OFFER_FIELDS = [["dryer", "Trockner"], ["folding", "Falten"], ["ironing", "Bügeln (inkl. Bügel-Pauschale 20 €)"], ["pickup", "Abholung beim Kunden"], ["return", "Rücklieferung zum Kunden"], ["softener", "Weichspüler"], ["perfume", "Wäscheparfum"], ["sensitive", "Sensitiv-Waschmittel"]];
const ATTR_FLAGS = [["smoke_free", "Rauchfreier Haushalt"], ["pet_free", "Haustierfreier Haushalt"], ["eco_detergent", "Öko-/Bio-Waschmittel"], ["allergy_friendly", "Allergikerfreundlich"], ["steam_ironing", "Dampfbügeln"], ["express", "Express (24 h)"], ["flexible_times", "Flexible Abholzeiten"]];

export function ServicesForm({ p, onSaved }) {
  const s = p.services || {};
  const a = p.attributes || {};
  const px = usePrices();
  const [f, setF] = useState({ dryer: !!s.dryer, folding: !!s.folding, ironing: !!s.ironing, pickup: !!s.pickup, return: !!s.return, softener: !!s.softener, perfume: !!s.perfume, sensitive: !!s.sensitive, self_pickup_allowed: s.self_pickup_allowed ?? true, offers_heavy_soil: s.offers_heavy_soil ?? true });
  const [at, setAt] = useState({ smoke_free: !!a.smoke_free, pet_free: !!a.pet_free, eco_detergent: !!a.eco_detergent, allergy_friendly: !!a.allergy_friendly, steam_ironing: !!a.steam_ironing, express: !!a.express, flexible_times: !!a.flexible_times, detergent: a.detergent || "", perfume_brand: a.perfume_brand || "", machines: a.machines || "" });
  const submit = (e) => { e.preventDefault(); save("/provider/me/services", { ...f, attributes: at }, onSaved); };
  return (
    <form className="space-y-5" onSubmit={submit}>
      <div className="rounded-2xl bg-mint-soft border border-mint p-3 text-sm font-semibold text-mint-ink" data-testid="ob-price-note">Der Waschpreis ist plattformweit fix: {eur(px?.base ?? 2.99)} je kg. Du wählst nur, welche Leistungen du anbietest – überzeugen tust du mit Service, Ausstattung und Bewertungen.</div>
      <div><p className="w-label">Welche Zusatzleistungen bietest du an?</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {OFFER_FIELDS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 rounded-xl border border-line p-3 cursor-pointer"><input type="checkbox" className="h-5 w-5 accent-[var(--w-mint-ink)]" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`ob-offer-${k}`} /> {l}</label>
          ))}
        </div>
      </div>
      <div><p className="w-label">Haushalt & Besonderheiten</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {ATTR_FLAGS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 rounded-xl border border-line p-3 cursor-pointer"><input type="checkbox" className="h-5 w-5 accent-[var(--w-mint-ink)]" checked={at[k]} onChange={(e) => setAt({ ...at, [k]: e.target.checked })} data-testid={`ob-attr-${k}`} /> {l}</label>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block"><span className="w-label">Waschmittel</span><input className="w-input" value={at.detergent} onChange={(e) => setAt({ ...at, detergent: e.target.value })} placeholder="z. B. Ecover Zero" data-testid="ob-attr-detergent" /></label>
        <label className="block"><span className="w-label">Wäscheparfum</span><input className="w-input" value={at.perfume_brand} onChange={(e) => setAt({ ...at, perfume_brand: e.target.value })} placeholder="z. B. Lenor" data-testid="ob-attr-perfume-brand" /></label>
        <label className="block"><span className="w-label">Geräte / Ausstattung</span><input className="w-input" value={at.machines} onChange={(e) => setAt({ ...at, machines: e.target.value })} placeholder="z. B. Miele + Trockner" data-testid="ob-attr-machines" /></label>
      </div>
      <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5 accent-[var(--w-mint-ink)]" checked={f.self_pickup_allowed} onChange={(e) => setF({ ...f, self_pickup_allowed: e.target.checked })} data-testid="ob-self-pickup" /> Selbstabgabe/-abholung durch Kunden erlauben</label>
      <label className="flex items-start gap-2"><input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={f.offers_heavy_soil} onChange={(e) => setF({ ...f, offers_heavy_soil: e.target.checked })} data-testid="ob-heavy-soil" /> <span>Anfragen für stark verunreinigte Wäsche (z. B. Blut, Urin, Stuhl) grundsätzlich anbieten. Jede einzelne Anfrage musst du trotzdem gesondert bestätigen.</span></label>
      <button className="w-btn w-btn-primary" data-testid="ob-services-save">Speichern</button>
    </form>
  );
}

export function CalendarForm({ p, onSaved }) {
  const c = p.calendar || {};
  const [weekly, setWeekly] = useState(c.weekly || {});
  const [cap, setCap] = useState(c.capacity_per_slot || 1);
  const [blocked, setBlocked] = useState((c.blocked_dates || []).join(", "));
  const toggle = (d, s) => {
    const cur = weekly[d] || [];
    setWeekly({ ...weekly, [d]: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  };
  const submit = (e) => { e.preventDefault(); save("/provider/me/calendar", { weekly, capacity_per_slot: +cap, blocked_dates: blocked.split(",").map((x) => x.trim()).filter(Boolean) }, onSaved); };
  return (
    <form className="space-y-4" onSubmit={submit}>
      <div className="overflow-x-auto">
        <table className="text-center"><thead><tr><th className="p-1" />{DAYS.map((d) => <th key={d} className="p-1 font-semibold">{d}</th>)}</tr></thead>
          <tbody>{SLOTS.map((s) => (
            <tr key={s}><th className="p-1 pr-3 text-left font-semibold whitespace-nowrap">{s}</th>
              {DAYS.map((d, i) => {
                const on = (weekly[String(i)] || []).includes(s);
                return <td key={d} className="p-1"><button type="button" aria-pressed={on} aria-label={`${d} ${s}`} onClick={() => toggle(String(i), s)} data-testid={`cal-${i}-${s}`}
                  className={`h-10 w-10 rounded-lg border font-bold ${on ? "bg-mint text-mint-on border-mint-ink" : "bg-white border-line"}`}>{on ? "✓" : ""}</button></td>;
              })}</tr>))}</tbody></table>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className="w-label">Kapazität je Zeitfenster</span><input type="number" min="1" max="10" className="w-input" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="cal-capacity" /></label>
        <label className="block"><span className="w-label">Gesperrte Tage (JJJJ-MM-TT, Komma)</span><input className="w-input" value={blocked} onChange={(e) => setBlocked(e.target.value)} data-testid="cal-blocked" /></label>
      </div>
      <p className="w-muted">Überbuchte Zeitfenster werden als Konflikt-Warnung angezeigt, Buchungen bleiben möglich.</p>
      <button className="w-btn w-btn-primary" data-testid="cal-save">Speichern</button>
    </form>
  );
}

export default function Onboarding({ p, setP }) {
  const onSaved = (data) => setP({ ...p, ...data });
  const submit = async () => {
    try { const { data } = await api.post("/provider/me/submit"); setP({ ...p, ...data }); toast.success("Zur Prüfung eingereicht"); } catch (e) { toast.error(errMsg(e)); }
  };
  const st = p.steps || {};
  const REQUIRED = [["profile", "Profil"], ["kyc", "Identitätsprüfung (KYC)"], ["business", "Gewerbestatus"], ["services", "Leistungen & Preise"], ["calendar", "Kalender"]];
  const missing = REQUIRED.filter(([k]) => !st[k]);
  const canSubmit = missing.length === 0;
  return (
    <div className="space-y-4">
      <Step n={1} title="Profil" done={st.profile} required testId="ob-step-profile"><ProfileForm p={p} onSaved={onSaved} /></Step>
      <Step n={2} title="Identitätsprüfung (KYC)" done={st.kyc} required testId="ob-step-kyc"><KycForm p={p} onSaved={onSaved} /></Step>
      <Step n={3} title="Gewerbestatus" done={st.business} required testId="ob-step-business"><BusinessForm p={p} onSaved={onSaved} /></Step>
      <Step n={4} title="Leistungen & Preise" done={st.services} required testId="ob-step-services"><ServicesForm p={p} onSaved={onSaved} /></Step>
      <Step n={5} title="Kalender" done={st.calendar} required testId="ob-step-calendar"><CalendarForm p={p} onSaved={onSaved} /></Step>
      {["draft", "rejected"].includes(p.status) && (
        <div className="w-card p-5 space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <span className="text-mint-ink">*</span>
            <p className="w-muted">Alle Schritte sind Pflicht, damit dich das Washly-Team freischalten kann.</p>
          </div>
          {!canSubmit && (
            <div className="rounded-xl bg-[var(--w-warn-soft,#fff7ed)] border border-[var(--w-warn,#f59e0b)] p-3 text-sm" data-testid="ob-missing-note">
              <p className="font-semibold mb-1">Noch nicht einreichbar – diese Pflichtschritte fehlen:</p>
              <ul className="list-disc pl-5">
                {missing.map(([k, l]) => <li key={k}>{l}</li>)}
              </ul>
            </div>
          )}
          <button className="w-btn w-btn-primary w-full" onClick={submit} disabled={!canSubmit} data-testid="ob-submit-btn">Zur Freigabe einreichen</button>
        </div>
      )}
    </div>
  );
}
